import { EntityBuilder, PartialPropsJson, PropsJson } from "@jobilla/entity";
import { OrderItem } from "@/entities/order-item";
import { api, Paginated } from "@/api/base";

export interface ExportToNexusRequest {
  organisation_id: string;
  catalogue_item_type: string;
  order_item_ids: string[];
}

export interface ExportToNexusResult {
  exported: Array<{ order_item_id: string; nexus_inventory_id: string | null }>;
  failed: Array<{ order_item_id: string; error: string }>;
}

export interface JOMCatalogueItem {
  id: string;
  type: string;
  name: string;
}

export async function fetchJOMCatalogueItems(): Promise<JOMCatalogueItem[]> {
  const json = await api.get("v1/orders/catalogue-items").json<{ data: JOMCatalogueItem[] }>();
  return json.data;
}

export interface OrderItemsFilter {
  product?: string;
  unit_id?: string;
  company_id?: string;
  statuses?: string[];
  per_page?: number;
  current_page?: number;
  order_by?: "created_at" | "campaign_created_at";
  order?: "asc" | "desc";
}

export async function fetchOrderItem(id: string): Promise<OrderItem> {
  const json = await api.get(`v1/orders/items/${id}`).json<{ data: PropsJson<OrderItem> }>();

  return EntityBuilder.buildOne(OrderItem, json.data);
}

export async function updateOrderItem(
  id: string,
  data: PartialPropsJson<OrderItem> & { amount?: number, product_type?: string },
) {
  return api.put(`v1/orders/items/${id}`, { json: data });
}

export async function splitOrderItem(
  id: string,
) {
  return api.post(`v1/orders/items/${id}/split`);
}

export async function listOrderItems(filters: OrderItemsFilter): Promise<Paginated<OrderItem[]>> {
  const searchParams: Record<string, string | number> = {
    per_page: filters.per_page ?? 25,
  };

  if (filters?.current_page) {
    searchParams["page"] = filters.current_page;
  }
  if (filters?.company_id) {
    searchParams["company_id"] = filters.company_id;
  }
  if (filters?.unit_id) {
    searchParams["unit_id"] = filters.unit_id;
  }
  if (filters?.product) {
    searchParams["product"] = filters.product;
  }
  if (filters?.order_by) {
    searchParams["order_by"] = filters.order_by;
  }
  if (filters?.order) {
    searchParams["order"] = filters.order;
  }
  filters?.statuses?.forEach((status, index) => {
    searchParams[`statuses[${index}]`] = status;
  });
  const response = await api
    .get("v1/orders/items", { searchParams: searchParams })
    .json<Paginated<PropsJson<OrderItem>[]>>();

  return {
    ...response,
    data: EntityBuilder.buildMany(OrderItem, response.data),
  };
}

export async function markAsMigrated(id: string): Promise<void> {
  await api.post(`v1/orders/items/${id}/mark-as-migrated`);
}

export async function exportToNexus(data: ExportToNexusRequest): Promise<ExportToNexusResult> {
  const response = await api.post("v1/orders/items/export-to-nexus", { json: data });
  const json = await response.json<{ data: ExportToNexusResult }>();
  return json.data;
}
